import React, { useState, useContext } from 'react';
import {
  Modal,
  ModalDialog,
  ModalOverflow,
  Typography,
  Stack,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  FormLabel,
  Input,
  Box,
  Alert
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';
import AddIcon from '@mui/icons-material/Add';
import StarIcon from '@mui/icons-material/Star';
import { LayoutContext } from '../context';
import { APIPurchaseLikes, APIPurchaseSuperLikes, APIPurchaseCombinedLikes } from '../api';



const PurchaseLikesModal = ({ onPurchaseSuccess }) => {
  const { token, purchaseModalOpen, setPurchaseModalOpen } = useContext(LayoutContext);
  const [step, setStep] = useState('selection');
  const [selectedType, setSelectedType] = useState(null);
  const [likesPacks, setLikesPacks] = useState(1);
  const [superLikesPacks, setSuperLikesPacks] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const resetModal = () => {
    setStep('selection');
    setSelectedType(null);
    setLikesPacks(1);
    setSuperLikesPacks(0);
    setError('');
  };

  const handleClose = () => {
    resetModal();
    setPurchaseModalOpen(false);
  };

  const calculateTotal = () => {
    return (likesPacks * 20) + (superLikesPacks * 50);
  };

  const handlePurchase = async () => {
    try {
      setProcessing(true);
      setError('');

      let response;
      if (selectedType === 'likes') {
        response = await APIPurchaseLikes(token, likesPacks);
      } else if (selectedType === 'super_likes') {
        response = await APIPurchaseSuperLikes(token, superLikesPacks);
      } else {
        response = await APIPurchaseCombinedLikes(token, likesPacks, superLikesPacks);
      }

      if (response.success) {
        // Открываем страницу оплаты
        window.open(response.payment_url, '_blank');
        setStep('payment');
        
        if (onPurchaseSuccess) {
          onPurchaseSuccess();
        }
      } else {
        setError(response.error || 'Ошибка создания платежа');
      }
    } catch (error) {
      setError('Ошибка при создании платежа');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <AnimatePresence>
      {purchaseModalOpen && (
        <Modal
            sx={{ zIndex: 10002 }}
            open={purchaseModalOpen}
            slotProps={{
                backdrop: {
                    component: motion.div,
                    initial: { opacity: 0, backdropFilter: 'blur(0px)' },
                    animate: { opacity: 1, backdropFilter: 'blur(8px)' },
                    exit: { opacity: 0, backdropFilter: 'blur(0px)' },
                    transition: { duration: 0.3, ease: "easeInOut" },
                }
            }}
        >
          <ModalOverflow>
            <ModalDialog
              component={motion.div}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              sx={{ maxWidth: 400, width: '90%' }}
            >
              <Typography level="h4" sx={{ textAlign: 'center', mb: 2 }}>
                Купить лайки
              </Typography>

              {error && (
                <Alert color="danger" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {step === 'selection' && (
                <SelectionStep
                  selectedType={selectedType}
                  setSelectedType={setSelectedType}
                  likesPacks={likesPacks}
                  setLikesPacks={setLikesPacks}
                  superLikesPacks={superLikesPacks}
                  setSuperLikesPacks={setSuperLikesPacks}
                  calculateTotal={calculateTotal}
                  onPurchase={handlePurchase}
                  processing={processing}
                />
              )}

              {step === 'payment' && (
                <PaymentStep onClose={handleClose} />
              )}
            </ModalDialog>
          </ModalOverflow>
        </Modal>
      )}
    </AnimatePresence>
  );
};

const SelectionStep = ({
  selectedType,
  setSelectedType,
  likesPacks,
  setLikesPacks,
  superLikesPacks,
  setSuperLikesPacks,
  calculateTotal,
  onPurchase,
  processing
}) => {
  return (
    <Stack spacing={3}>
      {/* Выбор типа покупки */}
      <Stack spacing={1}>
        <Typography level="body-sm" fontWeight="bold">
          Выберите тип:
        </Typography>
        <Stack direction="row" spacing={1}>
          <Chip
            variant={selectedType === 'likes' ? 'solid' : 'outlined'}
            color="primary"
            onClick={() => setSelectedType('likes')}
            sx={{ cursor: 'pointer', flex: 1, justifyContent: 'center' }}
          >
            Лайки
          </Chip>
          <Chip
            variant={selectedType === 'super_likes' ? 'solid' : 'outlined'}
            color="warning"
            onClick={() => setSelectedType('super_likes')}
            sx={{ cursor: 'pointer', flex: 1, justifyContent: 'center' }}
          >
            Суперлайки
          </Chip>
          <Chip
            variant={selectedType === 'both' ? 'solid' : 'outlined'}
            color="success"
            onClick={() => setSelectedType('both')}
            sx={{ cursor: 'pointer', flex: 1, justifyContent: 'center' }}
          >
            Оба
          </Chip>
        </Stack>
      </Stack>

      {/* Количество лайков */}
      {(selectedType === 'likes' || selectedType === 'both') && (
        <FormControl>
          <FormLabel>Пакеты лайков (5 лайков = 20 руб)</FormLabel>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Button
              variant="outlined"
              onClick={() => setLikesPacks(Math.max(1, likesPacks - 1))}
              disabled={likesPacks <= 1}
            >
              -
            </Button>
            <Typography sx={{ minWidth: '40px', textAlign: 'center' }}>
              {likesPacks}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setLikesPacks(likesPacks + 1)}
            >
              +
            </Button>
            <Typography level="body-sm" sx={{ ml: 2 }}>
              {likesPacks * 5} лайков
            </Typography>
          </Stack>
        </FormControl>
      )}

      {/* Количество суперлайков */}
      {(selectedType === 'super_likes' || selectedType === 'both') && (
        <FormControl>
          <FormLabel>Пакеты суперлайков (5 суперлайков = 50 руб)</FormLabel>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Button
              variant="outlined"
              onClick={() => setSuperLikesPacks(Math.max(0, superLikesPacks - 1))}
              disabled={superLikesPacks <= 0}
            >
              -
            </Button>
            <Typography sx={{ minWidth: '40px', textAlign: 'center' }}>
              {superLikesPacks}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setSuperLikesPacks(superLikesPacks + 1)}
            >
              +
            </Button>
            <Typography level="body-sm" sx={{ ml: 2 }}>
              {superLikesPacks * 5} суперлайков
            </Typography>
          </Stack>
        </FormControl>
      )}

      {/* Итоговая стоимость */}
      {selectedType && (
        <Card variant="soft">
          <CardContent>
            <Stack spacing={1}>
              <Typography level="title-sm">Итого:</Typography>
              {likesPacks > 0 && (
                <Typography level="body-sm">
                  {likesPacks} пак. лайков: {likesPacks * 20} руб
                </Typography>
              )}
              {superLikesPacks > 0 && (
                <Typography level="body-sm">
                  {superLikesPacks} пак. суперлайков: {superLikesPacks * 50} руб
                </Typography>
              )}
              <Typography level="title-lg" color="primary">
                Всего: {calculateTotal()} руб
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Кнопка покупки */}
      {selectedType && (
        <Button
          size="lg"
          onClick={onPurchase}
          loading={processing}
          disabled={!selectedType || (likesPacks === 0 && superLikesPacks === 0)}
        >
          Купить за {calculateTotal()} руб
        </Button>
      )}
    </Stack>
  );
};

const PaymentStep = ({ onClose }) => (
  <Stack spacing={3} alignItems="center" textAlign="center">
    <Typography level="h4" color="primary">
      Ожидание оплаты
    </Typography>
    <Typography level="body-md">
      Страница оплаты открылась в новой вкладке. После завершения оплаты вы можете закрыть это окно.
    </Typography>
    <Button onClick={onClose}>
      Закрыть
    </Button>
  </Stack>
);

export default PurchaseLikesModal;